'use client';

import * as React from 'react';
import { BaseModal, BaseModalProps } from './BaseModal';
import { CancelButton, CTAButton } from '@/components/ui/buttons';

/**
 * FormModal Props Interface
 * Extends BaseModal with form-specific features
 */
interface FormModalProps extends Omit<BaseModalProps, 'footer'> {
  /**
   * Callback when form is submitted
   * Can be async - loading state is handled automatically
   */
  onSubmit?: (e: React.FormEvent) => void | Promise<void>;

  /**
   * Submit button label
   * @default 'Save'
   */
  submitLabel?: string;

  /**
   * Cancel button label
   * @default 'Cancel'
   */
  cancelLabel?: string;

  /**
   * Whether the form is submitting
   * Shows spinner on submit button and disables actions
   * @default false
   */
  isSubmitting?: boolean;

  /**
   * Whether the submit button is disabled
   * @default false
   */
  isSubmitDisabled?: boolean;

  /**
   * Hide the cancel button
   * @default false
   */
  hideCancelButton?: boolean;

  /**
   * Custom footer content (replaces default buttons)
   * If provided, onSubmit and button props are ignored
   */
  customFooter?: React.ReactNode;

  /**
   * Callback when cancel is clicked
   * Defaults to closing the modal
   */
  onCancel?: () => void;

  /**
   * Form ID to associate with form element
   * Useful when form is not a direct child
   */
  formId?: string;
}

/**
 * FormModal - Specialized modal for forms
 *
 * A modal optimized for form submissions with built-in loading states,
 * submit/cancel buttons, and form validation support.
 *
 * Features:
 * - Automatic loading state management
 * - Submit/Cancel button handling
 * - Form element integration
 * - Async submit support
 * - Disabled state handling
 * - Customizable button labels and variants
 *
 * @example Basic form modal
 * ```tsx
 * const modal = useModal();
 * const [isSubmitting, setIsSubmitting] = useState(false);
 *
 * <FormModal
 *   open={modal.open}
 *   onOpenChange={modal.setOpen}
 *   title="Create Persona"
 *   description="Define targeting criteria"
 *   size="lg"
 *   isSubmitting={isSubmitting}
 *   onSubmit={async (e) => {
 *     e.preventDefault();
 *     setIsSubmitting(true);
 *     try {
 *       await createPersona(formData);
 *       modal.closeModal();
 *     } finally {
 *       setIsSubmitting(false);
 *     }
 *   }}
 * >
 *   <form>
 *     <Input name="name" label="Persona Name" />
 *     <Select name="seniority" label="Seniority Level" />
 *   </form>
 * </FormModal>
 * ```
 *
 * @example With React Hook Form
 * ```tsx
 * const modal = useModal();
 * const { handleSubmit, formState } = useForm();
 *
 * <FormModal
 *   open={modal.open}
 *   onOpenChange={modal.setOpen}
 *   title="Edit Audience"
 *   submitLabel="Update"
 *   isSubmitting={formState.isSubmitting}
 *   isSubmitDisabled={!formState.isValid}
 *   onSubmit={handleSubmit(async (data) => {
 *     await updateAudience(data);
 *     modal.closeModal();
 *   })}
 * >
 *   <AudienceForm />
 * </FormModal>
 * ```
 *
 * @example Custom footer
 * ```tsx
 * <FormModal
 *   open={open}
 *   onOpenChange={setOpen}
 *   title="Multi-step Form"
 *   customFooter={
 *     <div className="flex justify-between w-full">
 *       <Button variant="outline" onClick={handleBack}>Back</Button>
 *       <div className="flex gap-2">
 *         <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
 *         <Button onClick={handleNext}>Next</Button>
 *       </div>
 *     </div>
 *   }
 * >
 *   <StepContent />
 * </FormModal>
 * ```
 */
export const FormModal = React.forwardRef<HTMLDivElement, FormModalProps>(
  (
    {
      children,
      onSubmit,
      submitLabel = 'Save',
      cancelLabel = 'Cancel',
      isSubmitting = false,
      isSubmitDisabled = false,
      hideCancelButton = false,
      customFooter,
      onCancel,
      onOpenChange,
      formId,
      ...baseModalProps
    },
    ref
  ) => {
    const handleCancel = () => {
      if (onCancel) {
        onCancel();
      } else {
        onOpenChange(false);
      }
    };

    const handleSubmit = async (e: React.FormEvent) => {
      if (onSubmit) {
        await onSubmit(e);
      }
    };

    // Default footer with submit/cancel buttons
    // Based on audiences best practices - use CancelButton and CTAButton
    const defaultFooter = (
      <div className="flex justify-end gap-2 w-full">
        {!hideCancelButton && (
          <CancelButton
            type="button"
            onClick={handleCancel}
            disabled={isSubmitting}
          >
            {cancelLabel}
          </CancelButton>
        )}
        <CTAButton
          type={formId ? 'submit' : 'button'}
          form={formId}
          onClick={formId ? undefined : handleSubmit}
          disabled={isSubmitting || isSubmitDisabled}
          isLoading={isSubmitting}
        >
          {submitLabel}
        </CTAButton>
      </div>
    );

    return (
      <BaseModal
        ref={ref}
        {...baseModalProps}
        onOpenChange={onOpenChange}
        footer={customFooter || defaultFooter}
        disableEscapeClose={isSubmitting}
        disableOverlayClose={isSubmitting}
      >
        {formId ? (
          // If formId is provided, wrap in form element
          <form id={formId} onSubmit={handleSubmit}>
            {children}
          </form>
        ) : (
          children
        )}
      </BaseModal>
    );
  }
);

FormModal.displayName = 'FormModal';

